import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Star, Download, CalendarDays, UserCheck, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { API, apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const when = (iso) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export default function HostDashboard() {
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [community, setCommunity] = useState("All");
  const [openRow, setOpenRow] = useState(null);
  const [checkin, setCheckin] = useState(null);

  const openCheckin = async (eventId) => {
    if (checkin?.event_id === eventId) return setCheckin(null);
    try {
      const r = await apiFetch(`/events/${eventId}/attendees`);
      setCheckin({ event_id: eventId, ...r });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const mark = async (eventId, userId, attended) => {
    try {
      const res = await apiFetch(`/events/${eventId}/checkin`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, attended }),
      });
      setCheckin((c) => ({
        ...c,
        attendees: c.attendees.map((a) => (a.user_id === userId ? { ...a, attended } : a)),
      }));
      setData((d) => ({
        ...d,
        events: d.events.map((e) =>
          e.event_id === eventId ? { ...e, attended_count: res.attended_count } : e
        ),
      }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    if (!user || (user.role !== "leader" && user.role !== "admin")) return;
    apiFetch(`/host/dashboard?community=${encodeURIComponent(community)}`).then(setData).catch(() => {});
  }, [user, community]);

  if (loading) return <div className="min-h-screen bg-white" />;

  if (!user || (user.role !== "leader" && user.role !== "admin")) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900">Hosts only</h1>
          <p className="mt-3 text-neutral-600" data-testid="host-denied">
            The host dashboard is for community leaders.
          </p>
          {!user && (
            <button onClick={login} data-testid="host-login" className="mt-6 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-700">
              Continue with Google
            </button>
          )}
        </div>
      </div>
    );
  }

  const t = data?.totals;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Host dashboard</h1>
        <p className="mt-2 text-sm text-neutral-500 md:text-lg">RSVPs and ratings across the events you run.</p>

        <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="host-totals">
          {[
            { label: "Events", value: t?.events ?? "—" },
            { label: "Upcoming", value: t?.upcoming ?? "—" },
            { label: "Total RSVPs", value: t?.rsvps ?? "—" },
            { label: "Avg rating", value: t?.average_rating ?? "—" },
          ].map((c) => (
            <div key={c.label} className="rounded-2xl border border-neutral-200 p-5">
              <p className="text-3xl font-extrabold tracking-tight text-neutral-900">{c.value}</p>
              <p className="mt-1 text-xs text-neutral-500">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <select
            data-testid="host-community-filter"
            value={community}
            onChange={(e) => setCommunity(e.target.value)}
            className="rounded-full border border-neutral-300 px-4 py-2.5 text-sm font-semibold"
          >
            <option value="All">All communities</option>
            {(data?.communities || []).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <Link
            to="/organize"
            className="rounded-full border border-neutral-300 px-4 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
          >
            Add or edit listings
          </Link>
        </div>

        <ul className="mt-6 divide-y divide-neutral-200" data-testid="host-events">
          {(data?.events || []).map((e) => (
            <li key={e.event_id} className="py-4" data-testid={`host-row-${e.event_id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
                  <Link to={`/events/${e.event_id}`} className="block truncate text-sm font-bold text-neutral-900 hover:underline">
                    {e.title}
                  </Link>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-neutral-500">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0" /> {when(e.start_at)} · {e.venue_name} · {e.category}
                  </p>
                </div>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-neutral-800">
                  <Users className="h-4 w-4 text-neutral-400" /> {e.rsvp_count}
                  <span className="text-xs font-normal text-neutral-400">/ {e.capacity}</span>
                </span>
                <span
                  className="flex items-center gap-1.5 text-sm font-semibold text-neutral-800"
                  aria-label={`${e.attended_count} checked in`}
                  data-testid={`attended-${e.event_id}`}
                >
                  <UserCheck className="h-4 w-4 text-[#4D7C0F]" /> {e.attended_count}{" "}
                  <span className="text-xs font-normal text-neutral-400">checked in</span>
                </span>
                {e.waitlist_count > 0 && (
                  <span
                    className="flex items-center gap-1.5 text-sm font-semibold text-[#4D7C0F]"
                    aria-label={`${e.waitlist_count} waiting`}
                    data-testid={`waiting-${e.event_id}`}
                  >
                    <ListOrdered className="h-4 w-4" /> {e.waitlist_count}{" "}
                    <span className="text-xs font-normal text-neutral-400">waiting</span>
                  </span>
                )}
                <button
                  onClick={() => openCheckin(e.event_id)}
                  data-testid={`checkin-btn-${e.event_id}`}
                  className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
                >
                  {checkin?.event_id === e.event_id ? "Close check-in" : "Check in"}
                </button>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-neutral-800">
                  <Star className={`h-4 w-4 ${e.average_rating ? "fill-[#84CC16] text-[#4D7C0F]" : "text-neutral-300"}`} />
                  {e.average_rating ?? "—"}
                  <span className="text-xs font-normal text-neutral-400">({e.rating_count})</span>
                </span>
                <a
                  href={`${API}/events/${e.event_id}/attendees?fmt=csv`}
                  data-testid={`rsvp-csv-${e.event_id}`}
                  className="flex items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
                >
                  <Download className="h-3.5 w-3.5" /> RSVPs
                </a>
                {e.comments.length > 0 && (
                  <button
                    onClick={() => setOpenRow(openRow === e.event_id ? null : e.event_id)}
                    data-testid={`notes-${e.event_id}`}
                    className="text-xs font-semibold text-[#4D7C0F] hover:underline"
                  >
                    {openRow === e.event_id ? "hide notes" : `${e.comments.length} note${e.comments.length === 1 ? "" : "s"}`}
                  </button>
                )}
              </div>
              {checkin?.event_id === e.event_id && (
                <div className="mt-3 rounded-2xl border border-neutral-200 p-4" data-testid={`checkin-panel-${e.event_id}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-neutral-500">
                    {checkin.attendees.filter((a) => a.attended).length} of {checkin.count} RSVPs checked in
                  </p>
                  <ul className="mt-3 divide-y divide-neutral-100">
                    {checkin.attendees.map((a) => (
                      <li key={a.user_id} className="flex flex-wrap items-center gap-3 py-2">
                        <span className="min-w-0 flex-1 truncate text-sm text-neutral-800">
                          <span className="font-semibold">{a.name || a.email}</span>
                          {a.company ? <span className="text-neutral-500"> · {a.company}</span> : null}
                        </span>
                        <button
                          onClick={() => mark(e.event_id, a.user_id, !a.attended)}
                          data-testid={`mark-${e.event_id}-${a.user_id}`}
                          className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors duration-200 ${
                            a.attended
                              ? "bg-[#B8FF2E] text-[#14210A] hover:bg-[#A2E80B]"
                              : "border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                          }`}
                        >
                          {a.attended ? "Attended" : "Mark attended"}
                        </button>
                      </li>
                    ))}
                    {checkin.attendees.length === 0 && (
                      <li className="py-2 text-sm text-neutral-500">No RSVPs yet.</li>
                    )}
                  </ul>
                  {checkin.waitlist?.length > 0 && (
                    <p className="mt-3 text-xs text-neutral-500" data-testid={`waitlist-names-${e.event_id}`}>
                      <span className="font-bold text-neutral-700">Waitlist:</span>{" "}
                      {checkin.waitlist.map((w) => w.name || w.email).join(" · ")}
                    </p>
                  )}
                </div>
              )}
              {openRow === e.event_id && (
                <ul className="mt-3 space-y-2" data-testid={`notes-list-${e.event_id}`}>
                  {e.comments.map((c, i) => (
                    <li key={i} className="rounded-xl bg-neutral-50 p-3 text-sm text-neutral-700">
                      <span className="font-bold">{c.rating}/5</span> · {c.comment}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
